import { Select, Spin } from 'antd';
import debounce from 'lodash/debounce';
import React, { FC, useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Query } from 'src/core/stores/data-store';
import { container } from 'src/inversify.config';
import { OrganizationSummaryStore } from 'src/stores/organizations-store';

const OrganizationsSelect: FC<
  {
    onChange?: (value: { value: string; label: string }) => void;
    onClear?: () => void;
    exclude?: string[];
    filterByIds?: string[];
    value?: { value: string; label: string };
    onOrganizationListChanged?: (organizationList: string[]) => void;
    onlyPartners?: boolean;
    disabled?: boolean;
  } & WithTranslation
> = ({ t, onChange, onClear, exclude, filterByIds, value, onlyPartners, onOrganizationListChanged, disabled }) => {
  const organizationStore = container.get(OrganizationSummaryStore);
  const organizationsState = organizationStore.state;
  const [currentValue, setValue] = useState(value ? { value: value.value, label: value.label || `Organization (${value.value})` } : undefined);

  const load = async (search: string | undefined = undefined) => {
    const filter = (exclude || []).length === 0 ? [] : [`id ne ${exclude?.join(' and id ne ')}`];
    filter.push(...((filterByIds || []).length === 0 ? [] : [`id eq ${filterByIds?.join(' or id eq ')}`]));
    if (search) {
      filter.push(`startsWith(toLower(displayName), '${search.toLowerCase()}')`);
    }

    await organizationStore.load({
      searchQuery: '',
      orderBy: [{ field: 'displayName', direction: 'Ascending', useProfile: false }],
      skip: 0,
      take: 1000000,
      filter,
    } as Query);

    if (onOrganizationListChanged) {
      onOrganizationListChanged(organizationsState.items.value.filter((o) => (onlyPartners ? o.isPartner : true)).map((o) => o.id));
    }
  };

  useEffect(() => {
    setValue(undefined);
    load();
  }, [filterByIds]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let nextValue = value || currentValue;
    if (nextValue && nextValue.value && (exclude || []).includes(nextValue?.value as string)) nextValue = undefined;
    if (filterByIds && !filterByIds.includes(nextValue?.value as string)) {
      nextValue = undefined;
      onClear && onClear();
    }
    setValue(nextValue ? { value: nextValue.value, label: nextValue.label || `Organization (${nextValue.value})` } : undefined);
  }, [exclude, organizationStore, value, filterByIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = debounce((value: string) => {
    load(value);
  }, 800);

  const onSelect = (value: { value: string; label: string }) => {
    setValue(value);
    onChange && onChange(value);
  };

  return (
    <Select
      disabled={disabled}
      loading={!!organizationsState.isBusy.get()}
      labelInValue
      placeholder={t('SelectOrganization')}
      onChange={onSelect}
      style={{ width: '100%', height: '100%' }}
      allowClear
      onClear={onClear}
      showSearch
      value={currentValue}
      notFoundContent={organizationsState.isBusy.get() ? <Spin size="small" /> : undefined}
      filterOption={false}
      showArrow={true}
      onSearch={onSearch}
      onDropdownVisibleChange={(visible) => {
        if (!visible) load();
      }}
    >
      {organizationsState.items.value
        .filter((o) => (onlyPartners ? o.isPartner : true))
        .map((o) => (
          <Select.Option key={o.id} value={o.id}>
            {o.displayName}
          </Select.Option>
        ))}
    </Select>
  );
};

export default withTranslation()(OrganizationsSelect);
